/** Whether this signed-in learner sees the Tutor at all: a runtime, per-user gate decided by the
 * server (Worker GET /tutor/availability, which checks its owner allowlist). It is navigation only:
 * every Tutor request is still authorized by the Worker, which ignores everyone off the allowlist.
 *
 * Fails closed: signed out, a network error, a 404 (Worker without the route) or any unexpected
 * answer is "no Tutor". A failed check (network error, 5xx, timeout; not an explicit answer) is retried with
 * backoff, so a Tutor hidden while its backend was restarting reappears without a reload. It never delays the app: the check runs after sign-in in the background,
 * and the last answer is cached per uid (both shown at once and re-checked on each page load; a "yes" shown
 * immediately and re-checked once per page load), so most sessions of most learners send nothing.
 *
 * VITE_TUTOR_UI_ENABLED is now only an override: "false" hides the Tutor in any build (an
 * emergency frontend switch); "true" shows it without asking, in development builds only (demo
 * app, Playwright). Unset (production): ask the server. */
import { useSyncExternalStore } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../../backend/utils/init-firebase";
import { getFirebaseAuthorizationHeaders } from "../../../backend/utils/firebase-auth-headers";
import { toSignedInFirebaseUser } from "../../../backend/utils/anonymous-read-session";
import { JUNGLE_TUTOR_API_BASE_URL } from "../../../constants/GlobalTypesAndEnumsAndConstants";

export interface TutorAvailabilityState {
  /** True only after the server said yes for this uid (or a fresh cached yes). */
  available: boolean;
  /** False while a first answer for this uid is still unknown. */
  resolved: boolean;
}
interface CachedAnswer { available: boolean; at: number }
export interface TutorAvailabilityDependencies {
  observe(listener: (uid: string | null) => void): () => void;
  ask(signal: AbortSignal): Promise<boolean>;
  storage: Pick<Storage, "getItem" | "setItem"> | null;
  now(): number;
  defer(work: () => void): void;
  /** Ask again after a failed check (a backend that is down or restarting). Absent: no retry. */
  retry?(work: () => void, delayMs: number): void;
}

const CACHE_PREFIX = "jungle.tutorAvailability.v1:";
const NEGATIVE_TTL_MS = 6 * 60 * 60 * 1000;
const POSITIVE_TTL_MS = 24 * 60 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 8000;
/** A failed check (not an answer) is retried at 2 s, 4 s, 8 s, 16 s, then every 30 s while the same user is signed
 * in: after a reboot the Tutor appears by itself once its backend is back, without a reload. */
export function tutorAvailabilityRetryDelayMs(attempt: number): number { return Math.min(2000 * 2 ** attempt, 30_000); }
const CLOSED: TutorAvailabilityState = { available: false, resolved: true };
const PENDING: TutorAvailabilityState = { available: false, resolved: false };

export function buildTimeTutorOverride(value: unknown, isDevelopment: boolean): boolean | null {
  if (value === "false") return false;
  if (value === "true" && isDevelopment) return true;
  return null;
}

function readCache(storage: TutorAvailabilityDependencies["storage"], uid: string, now: number): CachedAnswer | null {
  try {
    const parsed = JSON.parse(storage?.getItem(CACHE_PREFIX + uid) ?? "null") as CachedAnswer | null;
    if (!parsed || typeof parsed.available !== "boolean" || typeof parsed.at !== "number") return null;
    const age = now - parsed.at;
    return age >= 0 && age < (parsed.available ? POSITIVE_TTL_MS : NEGATIVE_TTL_MS) ? parsed : null;
  } catch { return null; }
}

function writeCache(storage: TutorAvailabilityDependencies["storage"], uid: string, answer: CachedAnswer) {
  try { storage?.setItem(CACHE_PREFIX + uid, JSON.stringify(answer)); } catch { /* private mode: no cache */ }
}

/** The gate's state machine, independent of React and Firebase (tested directly). */
export function createTutorAvailabilityStore(dependencies: TutorAvailabilityDependencies) {
  let state: TutorAvailabilityState = PENDING, started = false, generation = 0, controller: AbortController | null = null;
  const listeners = new Set<() => void>();
  const set = (next: TutorAvailabilityState) => {
    if (next.available === state.available && next.resolved === state.resolved) return;
    state = next;
    for (const listener of [...listeners]) listener();
  };
  function onUser(uid: string | null) {
    const current = ++generation;
    controller?.abort(); controller = null;
    if (!uid) { set(CLOSED); return; }
    const cached = readCache(dependencies.storage, uid, dependencies.now());
    set(cached ? { available: cached.available, resolved: true } : PENDING);
    // Both cached answers are shown at once and re-checked once per page load: a "no" cached while the Worker
    // was restarting or before enrollment must not hide the Tutor for hours.
    const check = (attempt: number) => {
      if (current !== generation) return;
      const request = new AbortController(); controller = request;
      const timer = setTimeout(() => request.abort(), REQUEST_TIMEOUT_MS);
      dependencies.ask(request.signal).then(available => {
        if (current !== generation) return;
        writeCache(dependencies.storage, uid, { available, at: dependencies.now() });
        set({ available, resolved: true });
      }, () => {
        if (current !== generation) return;
        // Unknown is "no", except that a still-valid cached yes survives a transient failure.
        set(cached ? { available: cached.available, resolved: true } : CLOSED);
        dependencies.retry?.(() => check(attempt + 1), tutorAvailabilityRetryDelayMs(attempt));
      }).finally(() => clearTimeout(timer));
    };
    dependencies.defer(() => check(0));
  }
  return {
    getSnapshot: () => state,
    subscribe(listener: () => void) {
      listeners.add(listener);
      if (!started) { started = true; dependencies.observe(onUser); }
      return () => { listeners.delete(listener); };
    },
  };
}

/** GET /tutor/availability: true only for an explicit 200 { available: true }. */
export async function askTutorAvailability(signal: AbortSignal, fetcher: typeof fetch = (input, init) => globalThis.fetch(input, init)): Promise<boolean> {
  const headers = await getFirebaseAuthorizationHeaders();
  const response = await fetcher(`${JUNGLE_TUTOR_API_BASE_URL}/tutor/availability`, { method: "GET", headers, signal, cache: "no-store" });
  if (response.status === 404) return false;
  if (!response.ok) throw new Error("tutor_availability_unavailable");
  const body = await response.json().catch(() => null) as { available?: unknown } | null;
  return body?.available === true;
}

const override = buildTimeTutorOverride(import.meta.env.VITE_TUTOR_UI_ENABLED, import.meta.env.DEV === true);
const store = override !== null ? null : createTutorAvailabilityStore({
  observe: listener => {
    // An auth object that cannot be observed (tests, a broken SDK) is "signed out": no Tutor.
    try { return onAuthStateChanged(auth, user => listener(toSignedInFirebaseUser(user)?.uid ?? null), () => listener(null)); }
    catch { listener(null); return () => undefined; }
  },
  ask: signal => askTutorAvailability(signal),
  storage: typeof window === "undefined" ? null : window.localStorage,
  now: () => Date.now(),
  defer: work => { setTimeout(work, 0); },
  retry: (work, delayMs) => { setTimeout(work, delayMs); },
});
const fixed: TutorAvailabilityState = { available: override === true, resolved: true };
const noop = () => () => undefined;

export function useTutorAvailability(): TutorAvailabilityState {
  return useSyncExternalStore(store ? store.subscribe : noop, store ? store.getSnapshot : () => fixed, () => fixed);
}

/** Navigation gate: show the Tutor's entries and route only when the server said yes. */
export function useTutorUiEnabled(): boolean {
  return useTutorAvailability().available;
}
